import { Router, Switch, Route } from "wouter";
import { useEffect } from "react";
import { initAnalytics, track } from "@/lib/analytics";
import { hasAnalyticsConsent } from "@/lib/consent";
import { CookieBanner } from "@/components/CookieBanner";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/auth/AuthProvider";
import RequireAuth from "@/components/auth/RequireAuth";
import { useAuth } from "@/auth/AuthProvider";
import Landing from "@/pages/landing";
import Dashboard from "@/pages/dashboard";
import PlotEditorPage from "@/pages/plot-editor";
import ProjectGallery from "@/pages/project-gallery";
import SharedPlot from "@/pages/shared-plot";
import NotFound from "@/pages/not-found";
import ProjectPage from "@/pages/project";
import ProjectDataPreview from "@/pages/project-data-preview";
import BillingSuccess from "@/pages/billing-success";
import BillingCancel from "@/pages/billing-cancel";
import AccountBillingPage from "@/pages/account";
import { usePageViews } from "@/hooks/use-pageviews";
import NewPlotRedirect from "@/routes/NewPlotRedirect";
import BootScreen from "@/components/BootScreen";
import routes from "@/lib/routes";
import HelpPage from "@/pages/help";
import FigureCompositionDevPage from "@/pages/figure-composition-dev";
import ProjectFiguresPage from "@/pages/project-figures";
import ProjectFigureEditorPage from "@/pages/project-figure-editor";
import { getBlogUrl } from "@/lib/urls";
import { TourManagerProvider } from "@/components/onboarding/TourManager";

function AppRoutes() {
  function resolveBlogUrl(pathRest: string = ''): string {
    const blogUrl = getBlogUrl(pathRest);
    return `${blogUrl}${window.location.search}${window.location.hash}`;
  }

  // Docs are served within this SPA at /help (no redirect)

  function BlogRedirect() {
    useEffect(() => {
      try {
        const rest = window.location.pathname.replace(/^\/blog/, '');
        const target = resolveBlogUrl(rest);
        if (target !== window.location.href) {
          window.location.replace(target);
        }
      } catch {}
    }, []);
    return null;
  }

  function TermsRedirect() {
    useEffect(() => {
      try {
        const target = routes.legal.terms() + window.location.search + window.location.hash;
        window.location.replace(target);
      } catch {}
    }, []);
    return null;
  }

  function PrivacyRedirect() {
    useEffect(() => {
      try {
        const target = routes.legal.privacy() + window.location.search + window.location.hash;
        window.location.replace(target);
      } catch {}
    }, []);
    return null;
  }

  function DataRedirect() {
    useEffect(() => {
      try {
        const target = routes.legal.data() + window.location.search + window.location.hash;
        window.location.replace(target);
      } catch {}
    }, []);
    return null;
  }

  // HelpRedirect removed - /help is handled by in-app HelpPage

  return (
    <Switch>
      {/* Redirect any /blog paths to the external blog app */}
      <Route path="/blog" component={BlogRedirect} />
      <Route path="/blog/*" component={BlogRedirect} />
      {/* Help page (in-app) */}
      <Route path="/help" component={HelpPage} />
      <Route path="/help/" component={HelpPage} />
      <Route path="/help/*" component={HelpPage} />
      {/* Legal pages - redirect to external blog site */}
      <Route path="/legal/terms" component={TermsRedirect} />
      <Route path="/legal/privacy" component={PrivacyRedirect} />
      <Route path="/legal/data" component={DataRedirect} />
      {/* Redirects for legacy paths */}
      <Route path="/terms" component={TermsRedirect} />
      <Route path="/privacy" component={PrivacyRedirect} />
      <Route path="/data" component={DataRedirect} />
      {/* Home is public. If user is logged in and visits Home intentionally, we do not auto-redirect. */}
      <Route path={routes.home()} component={Landing} />

      {/* Project browsing */}
      <Route path={routes.dashboard()} component={Dashboard} />
      <Route path={routes.account()} component={AccountBillingPage} />
      <Route path={routes.billing.success()} component={BillingSuccess} />
      <Route path={routes.billing.cancel()} component={BillingCancel} />
      <Route path={routes.project.root(':projectId' as any)} component={ProjectPage} />
      <Route path={routes.project.gallery(':projectId' as any)} component={ProjectGallery} />
      <Route path={routes.project.file(':projectId' as any, ':fileId' as any)} component={ProjectDataPreview} />
      {/* Figure routes - more specific routes first */}
      <Route path={routes.project.figures.new(':projectId' as any)}>
        <RequireAuth>
          <ProjectFigureEditorPage />
        </RequireAuth>
      </Route>
      <Route path={routes.project.figures.edit(':projectId' as any, ':figureId' as any)}>
        <RequireAuth>
          <ProjectFigureEditorPage />
        </RequireAuth>
      </Route>
      <Route path={routes.project.figures.list(':projectId' as any)}>
        <RequireAuth>
          <ProjectFiguresPage />
        </RequireAuth>
      </Route>

      {/* Create-new plot route renders BootScreen and redirects to the created plot */}
      <Route path={routes.plotEditor.new()}>
        <RequireAuth>
          <NewPlotRedirect />
        </RequireAuth>
      </Route>

      {/* Existing editor pages require auth */}
      <Route path={routes.plotEditor.open(':projectId' as any)}>
        <RequireAuth>
          <PlotEditorPage />
        </RequireAuth>
      </Route>
      <Route path={routes.plotEditor.open(':projectId' as any, ':plotId' as any)}>
        <RequireAuth>
          <PlotEditorPage />
        </RequireAuth>
      </Route>

      <Route path={routes.share(':token' as any)} component={SharedPlot} />
      {/* Dev-only: Figure Composition Editor */}
      <Route path="/figure-composition-dev" component={FigureCompositionDevPage} />
      {/* Help route handled above */}
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  useEffect(() => {
  	try {
  		const path = typeof window !== 'undefined' ? (window.location.pathname || '') : '';
  		if (path.startsWith('/blog')) {
        const rest = path.replace(/^\/blog/, '');
        const target = getBlogUrl(rest) + window.location.search + window.location.hash;
        if (target !== window.location.href) {
          window.location.replace(target);
          return; // prevent SPA boot work; we're leaving
        }
      }
      // Redirect legacy legal paths to external blog site
      if (path === '/terms' || path.startsWith('/legal/terms')) {
        const target = routes.legal.terms() + window.location.search + window.location.hash;
        if (target !== window.location.href) {
          window.location.replace(target);
          return;
        }
      } else if (path === '/privacy' || path.startsWith('/legal/privacy')) {
        const target = routes.legal.privacy() + window.location.search + window.location.hash;
        if (target !== window.location.href) {
          window.location.replace(target);
          return;
        }
      } else if (path === '/data' || path.startsWith('/legal/data')) {
        const target = routes.legal.data() + window.location.search + window.location.hash;
        if (target !== window.location.href) {
          window.location.replace(target);
          return;
        }
      }
  	} catch {}
  	// Initialize analytics only if user has consented
  	if (hasAnalyticsConsent()) {
  		try {
  	initAnalytics();
  			// Minimal anonymous page view with UTM/referrer capture
  			const url = new URL(window.location.href);
  			const utm = {
  				utm_source: url.searchParams.get('utm_source') || undefined,
  				utm_medium: url.searchParams.get('utm_medium') || undefined,
  				utm_campaign: url.searchParams.get('utm_campaign') || undefined,
  				utm_term: url.searchParams.get('utm_term') || undefined,
  				utm_content: url.searchParams.get('utm_content') || undefined,
  			} as any;
  			try {
  				localStorage.setItem('lumi_utm', JSON.stringify({
  					referrer_host: document.referrer ? new URL(document.referrer).host : null,
  					...utm,
  				}));
  			} catch {}
  			try {
  				track('page:viewed', {
  					url_path: window.location.pathname,
  					referrer_host: document.referrer ? new URL(document.referrer).host : null,
  					...utm,
  				} as any);
  			} catch {}
  		} catch {}
  	}
  	try {
  		if (typeof window !== 'undefined' && window.location.pathname === '/' && window.location.hash === '#examples') {
  			window.location.replace(getBlogUrl());
  		}
  		const onHashChange = () => {
  			if (window.location.pathname === '/' && window.location.hash === '#examples') {
  				window.location.replace(getBlogUrl());
  			}
  		};
  		window.addEventListener('hashchange', onHashChange);
  		return () => window.removeEventListener('hashchange', onHashChange);
  	} catch {}
  }, []);
  usePageViews();
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <TourManagerProvider>
            <Toaster />
            <RootContent />
            <CookieBanner />
          </TourManagerProvider>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

function RootContent() {
  const { initializing } = useAuth();
  const bypass = (() => {
    try { const u = new URL(window.location.href); return u.searchParams.get('stub') === '1' || u.searchParams.get('e2e') === '1'; } catch { return false; }
  })();
  if (!bypass && initializing) return <BootScreen />;
  // Configure wouter's Router with the browser history (clean URLs, no hash)
  return (
    <Router>
      <AppRoutes />
    </Router>
  );
}

export default App;
